import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { BookOpen, Users, ArrowRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

// Lists a user's saved Search/Ask reports as clickable cards.
// Followers open them (owner earns 15%); owner opens free. Opens on the Search page.
export default function UserReports({ ownerId, isOwner = false, title = "Search & Ask reports" }) {
  const [reports, setReports] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!ownerId) return;
    api.get("/reports", { params: { owner: ownerId, limit: 12 } })
      .then(({ data }) => setReports(data.reports || []))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [ownerId]);

  if (!loaded) return null;
  if (reports.length === 0) {
    if (!isOwner) return null;
    return (
      <div className="rud-card rounded-2xl p-6" data-testid="user-reports-empty">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-2"><BookOpen size={14} /> {title}</div>
        <p className="text-sm text-sec">You haven't saved any reports yet. Run a Search and hit <b>Save & Earn</b> — then earn 15% every time someone opens it.</p>
        <Link to="/search" className="btn-primary h-9 px-4 text-sm inline-flex items-center gap-1.5 mt-3">Go to Search <ArrowRight size={14} /></Link>
      </div>
    );
  }

  return (
    <div className="rud-card rounded-2xl p-6" data-testid="user-reports">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-brand mb-1"><BookOpen size={14} /> {title}</div>
      <p className="text-xs text-sec mb-4">
        {isOwner
          ? "Reports you saved on My Kind. Anyone who opens one spends credits (0.25 personal · 0.5 professional) and you earn 15%."
          : "Open a ready-made report — costs a few credits, and the author earns 15%."}
      </p>
      <div className="grid sm:grid-cols-2 gap-3">
        {reports.map((r) => (
          <button key={r.id} data-testid={`user-report-${r.id}`} onClick={() => navigate(`/search?report=${r.id}`)}
            className="text-left rounded-xl border border-rud bg-elevated p-4 hover:border-brand/50 transition-colors group">
            <div className="flex items-start gap-2">
              <Sparkles size={14} className="text-brand mt-0.5 shrink-0" />
              <div className="min-w-0">
                <div className="font-medium text-sm group-hover:text-brand transition-colors line-clamp-2">{r.title || r.query}</div>
                {r.snippet && <p className="text-xs text-sec mt-1 line-clamp-2">{r.snippet}</p>}
              </div>
            </div>
            <div className="flex items-center gap-3 mt-3 pt-3 border-t border-rud text-[11px] text-sec">
              <span className="flex items-center gap-1"><Users size={11} /> {r.uses} uses</span>
              {r.scope && <span className="capitalize text-brand/80">{r.scope}</span>}
              {isOwner && <span className="text-brand">earned {r.earned} cr</span>}
              <span className="ml-auto flex items-center gap-1 text-brand">{isOwner ? "Open" : `Open · ${r.open_cost ?? 0.25} cr`} <ArrowRight size={11} /></span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
